import axios, { AxiosInstance, AxiosResponse } from "axios";
import {
    UserListItem,
    UserManagementListResponse,
    UserRole,
    UserWarehouse,
} from "../../@types/DRO/UserManagementResponse";
import {
    CreateUserPayload,
    UpdateUserPayload,
} from "../../@types/DTO/UserManagementBody";
import { ERP_BASE_URL } from "../../config/apiConfig";

export class UserManagementApi {
  public static api = new UserManagementApi();
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: ERP_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  private getToken() {
    return localStorage.getItem("token");
  }

  private getAuthHeaders() {
    return {
      Authorization: `Bearer ${this.getToken()}`,
    };
  }

  private extractArray<T>(payload: unknown, keys: string[]): T[] {
    if (Array.isArray(payload)) {
      return payload as T[];
    }

    if (!payload || typeof payload !== "object") {
      return [];
    }

    const root = payload as Record<string, unknown>;

    for (const key of keys) {
      const value = root[key];
      if (Array.isArray(value)) {
        return value as T[];
      }
    }

    const data = root.data;
    if (Array.isArray(data)) {
      return data as T[];
    }

    if (!data || typeof data !== "object") {
      return [];
    }

    const dataObject = data as Record<string, unknown>;
    for (const key of keys) {
      const value = dataObject[key];
      if (Array.isArray(value)) {
        return value as T[];
      }
    }

    return [];
  }

  async getRoles(): Promise<UserRole[]> {
    try {
      const response: AxiosResponse<UserManagementListResponse<UserRole>> =
        await this.instance.get(
        "/erp/api/user/fetch/roles",
        {
          headers: this.getAuthHeaders(),
        }
      );

      return this.extractArray<UserRole>(response.data, [
        "roles",
        "roleList",
        "userRoleDtoList",
      ]);
    } catch (error) {
      console.error("Error fetching user roles", error);
      throw error;
    }
  }

  async getWarehouses(): Promise<UserWarehouse[]> {
    try {
      const response: AxiosResponse<UserManagementListResponse<UserWarehouse>> =
        await this.instance.get(
        "erp/api/user/fetch/warehouses",
        {
          headers: this.getAuthHeaders(),
        }
      );

      return this.extractArray<UserWarehouse>(response.data, [
        "warehouses",
        "warehouseList",
        "warehouseDtoList",
      ]);
    } catch (error) {
      console.error("Error fetching warehouses", error);
      throw error;
    }
  }

  async createUser(payload: CreateUserPayload): Promise<void> {
    try {
      await this.instance.post("erp/api/user/create/erp/user", payload, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error("Error creating user", error);
      throw error;
    }
  }

  async getUsers(search: string, page: number, size: number): Promise<UserListItem[]> {
    try {
      const response: AxiosResponse<UserManagementListResponse<UserListItem>> =
        await this.instance.get("/erp/api/user/get/users", {
          headers: this.getAuthHeaders(),
          params: {
            search,
            page,
            size,
          },
        });

      return this.extractArray<UserListItem>(response.data, ["users", "userList"]);
    } catch (error) {
      console.error("Error fetching users", error);
      throw error;
    }
  }

  async updateUser(id: number, payload: UpdateUserPayload): Promise<void> {
    try {
      await this.instance.put(`erp/api/user/update/erp/user/${id}`, payload, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error("Error updating user", error);
      throw error;
    }
  }
}
